<template>
  <div data-qa="appointment-restrictions">
    <CoreDialog
      v-model="isOpenedRequestDialog"
      data-qa="appointment-restrictions-request-modal"
      size="sm"
    >
      <div
        class="d-flex flex-column align-center justify-center py-10"
        data-qa="modal-content-progress"
      >
        <v-progress-circular
          width="2.5"
          size="42"
          color="primary"
          indeterminate
        />
      </div>
    </CoreDialog>
    <v-bottom-sheet
      v-if="isMobile"
      v-model="isOpenedContentDialog"
      scrollable
    >
      <div
        data-qa="appointment-restrictions-modal-body"
        class="appointment-restrictions appointment-restrictions--mobile bg-gray-0 d-flex flex-column"
      >
        <div class="text-h6 text--text pt-6 px-4 pb-4">
          {{ TEXT.restrictions }}
        </div>
        <ModalContent
          class="px-4 pb-4"
          :is-opened="isOpenedContentDialog"
          :lpu-name="lpuName"
          :restrictions-data-list="appointmentRestrictionsDataList"
        />
        <div class="pa-2">
          <v-btn
            color="primary"
            class="branding-btn branding-btn--primary"
            depressed
            block
            @click="isOpenedContentDialog = false"
          >
            {{ TEXT.confirm }}
          </v-btn>
        </div>
      </div>
    </v-bottom-sheet>
    <CoreDialog
      v-else
      v-model="isOpenedContentDialog"
      size="md"
      :title="TEXT.restrictions"
      content-class="appointment-restrictions-modal"
      data-qa="appointment-restrictions-modal"
      divider
    >
      <ModalContent
        :is-opened="isOpenedContentDialog"
        :lpu-name="lpuName"
        :restrictions-data-list="appointmentRestrictionsDataList"
      />
      <template #actions>
        <v-btn
          data-qa="modal-actions-btn-close"
          color="primary"
          class="branding-btn branding-btn--primary"
          block
          depressed
          @click="isOpenedContentDialog = false"
        >
          {{ TEXT.confirm }}
        </v-btn>
      </template>
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import I18NextVue from 'i18next'
import { ref } from 'vue'
import isNetworkDisconnect from 'www/utils/is/isNetworkDisconnect'
import dispatchCustomEventGlobally from 'www/utils/withDependences/dispatchCustomEventGlobally'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import ModalContent from 'www/vue_components/common/AppointmentRestrictions/components/ModalContent.vue'
import type { OpenEventDetail, RestrictionsData } from 'www/vue_components/common/AppointmentRestrictions/types'
import apiAppointmentRestrictions from 'www/vue_components/common/AppointmentRestrictions/api/apiAppointmentRestrictions'
import getDoctorAppointmentRestrictionsDataList from 'www/vue_components/common/AppointmentRestrictions/functions/getDoctorAppointmentRestrictions'

const { t, language } = I18NextVue
const options = { ns: language }

const isMobile = window.MOBILE_VERSION
const isOpenedRequestDialog = ref(false)
const isOpenedContentDialog = ref(false)

const lpuName = ref<string>()
const responseMap = new Map<string, RestrictionsData[]>()
const appointmentRestrictionsDataList = ref<RestrictionsData[]>([])
const TEXT = {
  restrictions: t('Ограничения на приём к врачу', options),
  confirm: t('Понятно', options),
}

async function open(payload: OpenEventDetail) {
  const keyMap = `${payload.doctor.id}:${payload.lpu.id}`
  const start = performance.now()
  const waitDelay = async () => {
    const remainingDelay = 500 - Math.trunc(performance.now() - start)

    // Задержка перед закрытием модалки, чтобы она не "мигала" при очень быстрых запросах
    if (remainingDelay > 0) {
      await new Promise(resolve => {
        setTimeout(resolve, remainingDelay)
      })
    }
  }

  isOpenedRequestDialog.value = true
  lpuName.value = payload.lpu.name

  try {
    if (!responseMap.has(keyMap)) {
      const data = await apiAppointmentRestrictions.get({
        doctor_id: payload.doctor.id,
        lpu_id: payload.lpu.id,
      })
        .then(data => getDoctorAppointmentRestrictionsDataList(data))

      responseMap.set(keyMap, data)
      await waitDelay()
    }

    appointmentRestrictionsDataList.value = responseMap.get(keyMap)!

    if (!appointmentRestrictionsDataList.value.length) {
      throw new Error('Empty list')
    }

    if (isOpenedRequestDialog.value) {
      isOpenedRequestDialog.value = false
      isOpenedContentDialog.value = true
    }
  } catch (error: any) {
    responseMap.delete(keyMap)

    await waitDelay()

    if (isOpenedRequestDialog.value) {
      isOpenedRequestDialog.value = false

      if (isNetworkDisconnect(error)) {
        dispatchCustomEventGlobally('dialog:open', {
          maxWidth: 304,
          persistent: false,
          noClickAnimation: true,
          title: t('Нет интернета', options),
          text: t('Проверьте соединение и обновите страницу', options),
          confirmText: t('Обновить', options),
          beforeConfirm: () => window.location.reload(),
        })
      } else {
        dispatchCustomEventGlobally('dialog:open', {
          maxWidth: 304,
          persistent: false,
          noClickAnimation: true,
          title: t('Что-то пошло не так', options),
          text: t('Попробуйте немного подождать и обновите страницу', options),
          confirmText: t('Обновить', options),
          beforeConfirm: () => window.location.reload(),
        })
      }
    }
  }
}

defineExpose({
  open,
})
</script>

<style lang="scss">
.appointment-restrictions-modal [data-qa="core-dialog-content"] > * {
  overflow-y: auto;
}
</style>

<style lang="scss" scoped>
.appointment-restrictions {
  &--mobile {
    max-height: 75vh;

    @supports (max-height: 75dvh) {
      max-height: 75dvh;
    }
  }
}
</style>
